import { format } from 'date-fns';
import { tss } from 'tss-react/mui';

import {
  ListItemAvatar,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';

import VideoIcon from '@mui/icons-material/Videocam';
import FolderIcon from '@mui/icons-material/FolderOpen';

import { Or2Link } from 'Components/navigation/Or2Link';
import type { GenericEventEventFragment as Event } from '@/gql/graphql';
import { GroupOrServiceAvatar } from '@/components/Avatar/GroupOrServiceAvatar';
import { Or2ListItem } from '@/components/data-display/Or2ListItem';

const useStyles = tss.create(({ theme }) => {
  return {
    // remove 56px minWidth of avatar list item
    eventAvatarRoot: {
      minWidth: 0,
    },
    listItemAction: {
      top: '-12px',
      transform: 'none',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: '100%',
      paddingLeft: theme.spacing(1),
      borderLeft: `1px solid ${theme.palette.divider}`,
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
    },
    timeText: { marginLeft: theme.spacing(2) },
    folderIconPadding: { marginTop: theme.spacing(0.5) },
    listItemSecondaryActionPadding: { paddingRight: '58px' },
  };
});

interface Props {
  event: Event;
  showOwner: boolean;
}

export const EventListItem = ({ event, showOwner }: Props) => {
  const { classes } = useStyles();
  const { owner, location, videoMeeting, folder, allDay } = event;

  const videoMeetingLink = videoMeeting?.joinUrl;

  const hasSecondaryActions = Boolean(videoMeetingLink || folder);

  return (
    <>
      <Typography
        component="span"
        className={classes.timeText}
        variant="caption"
        color="textSecondary"
      >
        {allDay ? 'All day' : format(new Date(event?.start || ''), 'p')}
      </Typography>

      <Or2ListItem
        alignItems="flex-start"
        classes={{
          secondaryAction: classes.listItemSecondaryActionPadding,
        }}
        hoverHighlight={false}
        secondaryAction={
          hasSecondaryActions && (
            <Box className={classes.listItemAction}>
              {videoMeetingLink ? (
                <Tooltip title="Join video meeting" placement="right">
                  <IconButton
                    edge="end"
                    aria-label="video-call"
                    size="small"
                    href={videoMeetingLink}
                    target="_blank"
                  >
                    <VideoIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              {folder ? (
                <Or2Link
                  href={`/g/${owner?.machineName}/docs/${folder._id}`}
                  noMuiStyle
                >
                  <Tooltip title="Go to meeting folder" placement="right">
                    <IconButton
                      edge="end"
                      aria-label="folder-icon"
                      size="small"
                      className={
                        videoMeetingLink ? classes.folderIconPadding : undefined
                      }
                    >
                      <FolderIcon />
                    </IconButton>
                  </Tooltip>
                </Or2Link>
              ) : null}
            </Box>
          )
        }
      >
        {showOwner && (
          <ListItemAvatar className={classes.eventAvatarRoot}>
            <GroupOrServiceAvatar
              iconUrl={owner?.iconUrl}
              groupOrServiceName={owner?.name}
              sx={{ minWidth: 0, marginRight: 1.5 }}
            />
          </ListItemAvatar>
        )}

        <ListItemText
          disableTypography
          primary={<Typography variant="body2">{event?.title}</Typography>}
          secondary={
            <>
              {location ? (
                <>
                  <Typography
                    component="span"
                    variant="caption"
                    color="textSecondary"
                  >
                    {location}
                  </Typography>
                  <br />
                </>
              ) : null}
              {showOwner && (
                <Typography
                  component="span"
                  variant="caption"
                  color="textSecondary"
                >
                  from {owner?.name}
                </Typography>
              )}
            </>
          }
        />
      </Or2ListItem>
    </>
  );
};
